import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { useActiveCompanyId } from './company';
import { matchBackend } from '@lendica/utils';
import type { ApiErrorPayload } from '@lendica/utils';
import { v2 } from '@lendica/api';
import { useCallback, useMemo } from 'react';

type CompanyAxiosConfig = AxiosRequestConfig & { unwrap?: boolean };

// TODO: remove v2 baseURL from prefix, and configure in app level axiosInstance once migration is complete
const v2BaseURL = matchBackend('v2');
const prefix = (companyId: string, url: string) =>
    `${v2BaseURL}/client/companies/${companyId}${url.startsWith('/') ? url : `/${url}`}`;

export function useCompanyApi(instance: AxiosInstance = axios) {
    const companyId = useActiveCompanyId();

    const buildUrl = useCallback((url: string) => prefix(companyId, url), [companyId]);

    const wrap = <T>(p: Promise<T>): Promise<T> =>
        p.catch((err: unknown) => {
            const maybeAxiosError = err as AxiosError<any> | undefined;
            const responseData = maybeAxiosError?.response?.data as ApiErrorPayload | undefined;

            let pickedMessage: string | undefined;
            if (responseData != null) {
                if (typeof responseData === 'string' && responseData.trim()) {
                    pickedMessage = responseData;
                } else if (
                    typeof (responseData as any)?.message === 'string' &&
                    (responseData as any).message.trim()
                ) {
                    pickedMessage = (responseData as any).message;
                } else if (
                    typeof (responseData as any)?.detail === 'string' &&
                    (responseData as any).detail.trim()
                ) {
                    pickedMessage = (responseData as any).detail;
                } else if (
                    typeof (responseData as any)?.error === 'string' &&
                    (responseData as any).error.trim()
                ) {
                    pickedMessage = (responseData as any).error;
                } else if (
                    Array.isArray((responseData as any)?.errors) &&
                    (responseData as any).errors.length > 0
                ) {
                    const first = (responseData as any).errors[0];
                    if (typeof first === 'string') pickedMessage = first;
                    else if (first?.message) pickedMessage = first.message;
                } else if (
                    responseData &&
                    typeof responseData === 'object' &&
                    (responseData as any).errors &&
                    typeof (responseData as any).errors === 'object'
                ) {
                    const firstKey = Object.keys((responseData as any).errors)[0];
                    const val = (responseData as any).errors[firstKey];
                    if (typeof val === 'string') pickedMessage = val;
                    else if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string')
                        pickedMessage = val[0];
                }
            }

            if (!pickedMessage) {
                if (maybeAxiosError?.message && maybeAxiosError.message.trim()) {
                    pickedMessage = maybeAxiosError.message;
                } else if (err instanceof Error && err.message.trim()) {
                    pickedMessage = err.message;
                }
            }

            const normalizedError = new Error(pickedMessage || 'Something went wrong.');
            if (maybeAxiosError?.response)
                (normalizedError as any).response = maybeAxiosError.response;
            if (maybeAxiosError?.request)
                (normalizedError as any).request = maybeAxiosError.request;
            if (maybeAxiosError?.config) (normalizedError as any).config = maybeAxiosError.config;
            if (maybeAxiosError?.code) (normalizedError as any).code = maybeAxiosError.code;
            const status = maybeAxiosError?.response?.status ?? (maybeAxiosError as any)?.status;
            if (typeof status === 'number') (normalizedError as any).status = status;
            if (responseData && typeof responseData === 'object') {
                // Ensure detail is still accessible
                (normalizedError as any).response = {
                    ...(normalizedError as any).response,
                    data: responseData,
                };
            }

            return Promise.reject(normalizedError);
        });

    const api = useMemo(() => {
        return {
            get<T = any>(url: string, config?: CompanyAxiosConfig) {
                if (!companyId) throw new Error('Active company not resolved');
                return wrap(
                    instance
                        .get(buildUrl(url), config)
                        .then(res =>
                            config?.unwrap === false ? (res.data as T) : v2.unwrap<T>(res.data)
                        )
                );
            },
            post<T = any>(url: string, data?: any, config?: CompanyAxiosConfig) {
                if (!companyId) throw new Error('Active company not resolved');
                return wrap(
                    instance
                        .post(buildUrl(url), data, config)
                        .then(res =>
                            config?.unwrap === false ? (res.data as T) : v2.unwrap<T>(res.data)
                        )
                );
            },
            put<T = any>(url: string, data?: any, config?: CompanyAxiosConfig) {
                if (!companyId) throw new Error('Active company not resolved');
                return wrap(
                    instance
                        .put(buildUrl(url), data, config)
                        .then(res =>
                            config?.unwrap === false ? (res.data as T) : v2.unwrap<T>(res.data)
                        )
                );
            },
            patch<T = any>(url: string, data?: any, config?: CompanyAxiosConfig) {
                if (!companyId) throw new Error('Active company not resolved');
                return wrap(
                    instance
                        .patch(buildUrl(url), data, config)
                        .then(res =>
                            config?.unwrap === false ? (res.data as T) : v2.unwrap<T>(res.data)
                        )
                );
            },
            delete<T = any>(url: string, config?: CompanyAxiosConfig) {
                if (!companyId) throw new Error('Active company not resolved');
                return wrap(
                    instance
                        .delete(buildUrl(url), config)
                        .then(res =>
                            config?.unwrap === false ? (res.data as T) : v2.unwrap<T>(res.data)
                        )
                );
            },
            request<T = any>(config: CompanyAxiosConfig) {
                if (!companyId) throw new Error('Active company not resolved');
                const { unwrap, ...axiosConfig } = config || {};
                return wrap(
                    instance
                        .request({ ...axiosConfig, url: buildUrl(config.url!) })
                        .then(res => (unwrap === false ? (res.data as T) : v2.unwrap<T>(res.data)))
                );
            },
        };
        // Only change when company or axios instance changes
    }, [buildUrl, instance]);

    return api;
}
